export const StepList = ({ steps, testId = "site-steps" }) => (
  <ol data-testid={testId}>
    {steps.map((s, i) => (
      <li
        key={s.title}
        className="grid grid-cols-[3rem_1fr] gap-6 border-t border-[color:var(--s-line)] py-8 last:border-b"
      >
        <span className="s-num text-2xl text-[color:var(--s-gold)]">
          {String(i + 1).padStart(2, "0")}
        </span>
        <span>
          <span className="s-h block text-base sm:text-lg">{s.title}</span>
          <span className="mt-2 block max-w-md text-sm leading-relaxed text-[color:var(--s-mute)]">
            {s.body}
          </span>
        </span>
      </li>
    ))}
  </ol>
);

const PER_ROW = 3;
const CENTRE = 100 / (PER_ROW * 2); // first badge sits a sixth in
const EDGE = 3; // how far past the outer badges the turn reaches
const LINE = "border-[color:var(--s-gold)]";

const chunk = (arr) =>
  arr.reduce((rows, s, i) => {
    if (i % PER_ROW === 0) rows.push([]);
    rows[rows.length - 1].push(s);
    return rows;
  }, []);

// Process laid out as a snaking line — three steps a row, then it loops back.
export const StepFlow = ({ steps, testId = "site-steps" }) => {
  const rows = chunk(steps);
  return (
    <ol data-testid={testId}>
      {rows.map((row, ri) => {
        const last = ri === rows.length - 1;
        const y = ri === 0 ? "2.5rem" : "6rem";
        return (
          <li
            key={ri}
            className={`relative grid gap-12 sm:grid-cols-2 sm:gap-x-8 lg:grid-cols-3 ${
              ri === 0 ? "" : "mt-12 lg:mt-0 lg:pt-14"
            }`}
          >
            <div className="pointer-events-none absolute inset-0 hidden lg:block">
              <span
                className={`absolute border-t ${LINE}`}
                style={{
                  top: y,
                  left: `${CENTRE}%`,
                  right: last
                    ? `${100 - CENTRE * (row.length * 2 - 1)}%`
                    : `${EDGE}%`,
                }}
              />
              {!last && (
                <span
                  className={`absolute rounded-br-[3.5rem] border-b border-r ${LINE}`}
                  style={{ top: y, bottom: "-1.5rem", left: `${EDGE}%`, right: `${EDGE}%` }}
                />
              )}
              {ri > 0 && (
                <span
                  className={`absolute rounded-bl-[3.5rem] border-b border-l ${LINE}`}
                  style={{
                    top: "1.5rem",
                    height: "4.5rem",
                    left: `${EDGE}%`,
                    right: `${100 - CENTRE}%`,
                  }}
                />
              )}
            </div>

            {row.map((s, i) => (
              <div
                key={s.title}
                data-testid={`site-step-${ri * PER_ROW + i}`}
                className="group relative flex flex-col items-center text-center"
              >
                <span className="s-num relative z-10 flex h-20 w-20 items-center justify-center rounded-full border border-[color:var(--s-line)] bg-[color:var(--s-surface)] text-base text-[color:var(--s-gold)] transition-colors duration-500 group-hover:border-[color:var(--s-gold-bright)] group-hover:bg-[color:var(--s-gold-bright)] group-hover:text-[color:var(--s-ink)]">
                  {String(ri * PER_ROW + i + 1).padStart(2, "0")}
                </span>
                <span className="s-h mt-6 block text-base sm:text-lg">{s.title}</span>
                <span className="mt-3 block max-w-[19rem] text-sm leading-relaxed text-[color:var(--s-mute)]">
                  {s.body}
                </span>
              </div>
            ))}
          </li>
        );
      })}
    </ol>
  );
};

export const Workflow = ({ steps, image, image2, micro, title, meta, cta }) => (
  <section
    className="bg-[color:var(--s-surface)] px-6 py-20 sm:py-28 lg:px-12"
    data-testid="site-workflow"
  >
    <div className="s-reveal mx-auto grid max-w-[2560px] gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.9fr)]">
      <div className="grid gap-4">
        <div className="flex flex-col justify-between  bg-[color:var(--s-cream)] p-7 sm:p-9">
          <div>
            {micro && <div className="s-eyebrow">{micro}</div>}
            <h2 className="s-h mt-5 text-2xl sm:text-3xl">{title}</h2>
            {meta && (
              <p className="mt-4 text-sm leading-relaxed text-[color:var(--s-mute)]">
                {meta}
              </p>
            )}
          </div>
          {cta && <div className="mt-10">{cta}</div>}
        </div>
        <div className="s-fig s-soft">
          <img
            src={image2 || image}
            alt=""
            className="aspect-[4/3] w-full object-cover"
          />
        </div>
      </div>
      <div className="s-fig s-soft relative min-h-[46vh] lg:min-h-[72vh]">
        {image && (
          <img
            src={image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
        <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-6 sm:p-8">
          <ol className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {steps.map((s, i) => (
              <li key={s.title} className="flex items-baseline gap-3 text-white">
                <span className="s-num text-sm text-[color:var(--s-gold-bright)]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="s-h text-xs sm:text-sm">{s.title}</span>
              </li>
            ))}
          </ol>
        </span>
      </div>
    </div>
  </section>
);
